import { Table, Badge, Pagination, Button } from "flowbite-react";
import { HiPencil, HiTrash, HiEye } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { KbSolution, DataGridPagination, VectorizeStatusType } from "@/sharedTypes";

const vectorizeBadgeColor: Record<VectorizeStatusType, string> = {
  not_vectorized: "gray",
  pending: "warning",
  processing: "purple",
  vectorized: "success",
  failed: "failure",
};

interface KbSolutionTableProps {
  data: KbSolution[];
  pagination: DataGridPagination;
  onPageChange: (page: number) => void;
  onEdit: (solution: KbSolution) => void;
  onDelete: (solution: KbSolution) => void;
  onView: (solution: KbSolution) => void;
}

export default function KbSolutionTable({
  data,
  pagination,
  onPageChange,
  onEdit,
  onDelete,
  onView,
}: KbSolutionTableProps) {
  const { t } = useTranslation(["knowledgeBase", "common"]);

  return (
    <div className="overflow-x-auto rounded-lg shadow">
      <Table hoverable>
        <Table.Head>
          <Table.HeadCell className="w-16">{t("knowledgeBase:id")}</Table.HeadCell>
          <Table.HeadCell>{t("knowledgeBase:solutionText")}</Table.HeadCell>
          <Table.HeadCell>{t("knowledgeBase:aiApproved")}</Table.HeadCell>
          <Table.HeadCell>{t("knowledgeBase:vectorized")}</Table.HeadCell>
          <Table.HeadCell>{t("knowledgeBase:actions")}</Table.HeadCell>
        </Table.Head>
        <Table.Body className="divide-y">
          {data.length === 0 ? (
            <Table.Row>
              <Table.Cell colSpan={5} className="text-center py-8 text-gray-500">
                {t("knowledgeBase:noSolutions")}
              </Table.Cell>
            </Table.Row>
          ) : (
            data.map((solution) => (
              <Table.Row
                key={solution.id}
                className="bg-white dark:border-gray-700 dark:bg-gray-800"
              >
                <Table.Cell className="w-16 text-gray-500 text-sm font-mono">
                  {solution.id}
                </Table.Cell>
                <Table.Cell className="max-w-md truncate font-medium text-gray-900 dark:text-white">
                  {solution.kb_solution_text}
                </Table.Cell>
                <Table.Cell>
                  <Badge color={solution.is_ai_approved ? "success" : "gray"} size="sm">
                    {solution.is_ai_approved ? t("knowledgeBase:yes") : t("knowledgeBase:no")}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <Badge color={vectorizeBadgeColor[solution.vectorize_status || "not_vectorized"]} size="sm">
                    {t(`knowledgeBase:vectorizeStatus.${solution.vectorize_status || "not_vectorized"}`)}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <div className="flex gap-2">
                    <Button size="xs" color="light" onClick={() => onView(solution)}>
                      <HiEye className="h-4 w-4" />
                    </Button>
                    <Button size="xs" color="light" onClick={() => onEdit(solution)}>
                      <HiPencil className="h-4 w-4" />
                    </Button>
                    <Button size="xs" color="failure" onClick={() => onDelete(solution)}>
                      <HiTrash className="h-4 w-4" />
                    </Button>
                  </div>
                </Table.Cell>
              </Table.Row>
            ))
          )}
        </Table.Body>
      </Table>
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-center py-4">
          <Pagination
            currentPage={pagination.pageIndex}
            totalPages={pagination.totalPages}
            onPageChange={onPageChange}
            showIcons
          />
        </div>
      )}
    </div>
  );
}
